import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Table, Input, Select, Button, message, Modal, Form, Dropdown, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import {
  TranslationContainer,
  SidebarStyled,
  SidebarTitle,
  SidebarSubtitle,
  SidebarSection,
  SidebarSectionScrollable,
  SidebarLabel,
  ScreenButton,
  MainContent,
  MetricsContainer,
  MetricCard,
  MetricLabel,
  MetricValue,
  SearchContainer,
  TabsContainer,
  TabButton,
  TableCard,
  StatusBadge,
  KeyCell,
  EmptyText,
} from "./translation-gestor.styled";
import { SearchOutlined, PlusOutlined, FileAddOutlined, MoreOutlined, EditOutlined, DeleteOutlined, CloseOutlined } from "@ant-design/icons";
import { getGestorTranslationPage, getScreenTranslations, createTranslation, updateTranslation, deleteTranslation } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { invalidateTranslationsCache } from "./translateValue";


// Tipos
type TranslationStatus = "complete" | "missing";

interface Language {
  code: string;
  label: string;
}

interface TranslationRow {
  id: number;
  screen: string;
  key: string;
  es?: string;
  en?: string;
  [key: string]: any; // Para soportar idiomas dinámicos
  status: TranslationStatus;
}

interface MissingRow {
  id: number;
  screen: string;
  key: string;
  baseValue: string;
}

// Datos iniciales vacíos - se cargarán desde el API
const INITIAL_SCREENS: string[] = [];
const INITIAL_TRANSLATIONS: TranslationRow[] = [];
const INITIAL_MISSING: MissingRow[] = [];

// Idiomas disponibles inicialmente
const INITIAL_LANGUAGES: Language[] = [
  { code: "en", label: "Inglés (EN)" },
  { code: "es", label: "Español (ES)" },
];

const TranslationGestor: React.FC = () => {
  const [selectedScreen, setSelectedScreen] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"translations" | "missing">("translations");
  const [search, setSearch] = useState<string>("");
  const [translations, setTranslations] = useState<TranslationRow[]>(INITIAL_TRANSLATIONS);
  const [missing, setMissing] = useState<MissingRow[]>(INITIAL_MISSING);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentMissing, setCurrentMissing] = useState<MissingRow | null>(null);
  const [form] = Form.useForm();
  const [translationModalVisible, setTranslationModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTranslation, setCurrentTranslation] = useState<TranslationRow | null>(null);
  const [translationForm] = Form.useForm();
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const [gestorTranslationPage, setGestorTranslationPage] = useState<any>(null);
  const { locale } = useLocale();
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>(INITIAL_LANGUAGES);
  const [addLanguageModalVisible, setAddLanguageModalVisible] = useState(false);
  const [addLanguageForm] = Form.useForm();
  const [isCreatingNewScreen, setIsCreatingNewScreen] = useState(false);
  const [availableScreens, setAvailableScreens] = useState<string[]>(INITIAL_SCREENS);
  const [isLoadingTranslations, setIsLoadingTranslations] = useState<boolean>(true);
  const [screenSearch, setScreenSearch] = useState<string>("");

  // Función para calcular el estado de una traducción
  const calculateTranslationStatus = (translation: any, selectedLang: string): TranslationStatus => {
    // Una traducción está completa si tiene español (base) y el idioma seleccionado
    return (translation.es && translation[selectedLang]) ? "complete" : "missing";
  };


  // Función para transformar los datos del API al formato del componente (memoizada)
  const transformApiData = useCallback((apiData: any, selectedLang: string) => {
    if (!apiData?.data) return { screens: [], translations: [], missing: [], languages: [] };

    const screens = apiData.data.screens || [];
    const apiTranslations = apiData.data.translations || [];

    // Detectar idiomas dinámicos de las traducciones
    const detectedLanguages = new Set<string>();
    detectedLanguages.add('es'); // Español siempre está presente
    apiTranslations.forEach((t: any) => {
      Object.keys(t).forEach((key) => {
        if (key !== 'id' && key !== 'screen' && key !== 'key' && typeof t[key] === 'string') {
          detectedLanguages.add(key);
        }
      });
    });

    // Crear array de idiomas detectados
    const labels: { [key: string]: string } = {
      es: 'Español (ES)',
      en: 'Inglés (EN)',
      de: 'Alemán (DE)',
      fr: 'Francés (FR)',
      it: 'Italiano (IT)',
      pt: 'Portugués (PT)',
    };
    const languages: Language[] = Array.from(detectedLanguages).map((code) => ({
      code,
      label: labels[code] || `${code.toUpperCase()}`,
    }));

    // Transformar traducciones del API al formato del componente
    const translations: TranslationRow[] = apiTranslations.map((t: any) => ({
      id: t.id,
      screen: t.screen,
      key: t.key,
      es: t.es,
      en: t.en,
      ...Object.keys(t).reduce((acc, key) => {
        if (key !== 'id' && key !== 'screen' && key !== 'key' && typeof t[key] === 'string') {
          acc[key] = t[key];
        }
        return acc;
      }, {} as any),
      status: calculateTranslationStatus(t, selectedLang),
    }));

    // Calcular missing: traducciones que no tienen el idioma seleccionado
    // Nota: El API no retorna missing directamente, así que lo calculamos
    const missing: MissingRow[] = [];
    // Si necesitamos missing, podemos agregarlo aquí basado en las traducciones que faltan

    return { screens, translations, missing, languages };
  }, []);

  // Cargar traducciones desde el API (solo al montar)
  useEffect(() => {
    let cancel = false;
    setIsLoadingTranslations(true);

    getScreenTranslations()
      .then((response) => {
        if (!cancel) {
          // El API retorna { responseStatus, message, data }
          // getScreenTranslations ya retorna response.data de axios, así que response es el objeto del servidor
          const apiData = response;
          
          if (apiData?.responseStatus === "Success" && apiData?.data) {
            // Usar el idioma seleccionado actual para la transformación inicial
            const currentLang = selectedLanguage;
            const transformed = transformApiData(apiData, currentLang);
            
            setAvailableScreens(transformed.screens);
            setTranslations(transformed.translations);
            setMissing(transformed.missing);
            
            // Actualizar idiomas disponibles con los detectados del API
            if (transformed.languages && transformed.languages.length > 0) {
              setAvailableLanguages(transformed.languages);
              if (!transformed.languages.find(l => l.code === currentLang)) {
                setSelectedLanguage(transformed.languages[0]?.code ?? "es");
              }
            }
            
            // Seleccionar la primera pantalla si hay pantallas disponibles y no hay una seleccionada
            if (transformed.screens.length > 0) {
              setSelectedScreen((prev) => {
                if (!prev || !transformed.screens.includes(prev)) {
                  return transformed.screens[0];
                }
                return prev;
              });
            }
          }
          setIsLoadingTranslations(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          setIsLoadingTranslations(false);
        }
      });

    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo se ejecuta al montar

  // Actualizar estados cuando cambia el idioma seleccionado
  useEffect(() => {
    setTranslations((prev) =>
      prev.map((t) => ({
        ...t,
        status: calculateTranslationStatus(t, selectedLanguage),
      }))
    );
  }, [selectedLanguage]);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setGestorTranslationPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getGestorTranslationPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setGestorTranslationPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setGestorTranslationPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  // Filtrar traducciones por pantalla y búsqueda (solo keys con traducción completa)
  // IMPORTANTE: Los hooks deben estar antes del early return
  const filteredTranslations = useMemo(() => {
    if (!selectedScreen) return [];
    
    // Filtrar solo las traducciones que tienen tanto español como el idioma seleccionado
    const completeTranslations = translations.filter((t) => {
      return (
        t.screen === selectedScreen &&
        t.es && // Tiene traducción en español
        t[selectedLanguage] // Tiene traducción en el idioma seleccionado
      );
    });
    
    // Aplicar filtro de búsqueda (incluye búsqueda en el idioma seleccionado)
    const searchLower = search.toLowerCase();
    return completeTranslations.filter((t) => {
      const selectedLangValue = t[selectedLanguage];
      const matchesSearch =
        !searchLower ||
        t.key.toLowerCase().includes(searchLower) ||
        (t.es && t.es.toLowerCase().includes(searchLower)) ||
        (selectedLangValue && String(selectedLangValue).toLowerCase().includes(searchLower));
      return matchesSearch;
    });
  }, [selectedScreen, search, translations, selectedLanguage]);

  // Métricas basadas en el idioma seleccionado
  // Calcular el total de keys (completas + pendientes) para las métricas
  const totalKeys = useMemo(() => {
    if (!selectedScreen) return 0;
    const allKeys = new Set<string>();
    // Agregar todas las keys de traducciones de la pantalla
    translations
      .filter((t) => t.screen === selectedScreen)
      .forEach((t) => allKeys.add(t.key));
    // Agregar keys de missing
    missing
      .filter((m) => m.screen === selectedScreen)
      .forEach((m) => allKeys.add(m.key));
    return allKeys.size;
  }, [selectedScreen, translations, missing]);
  
  // Las traducciones completas son las que tienen tanto es como el idioma seleccionado
  const completeCount = useMemo(() => {
    if (!selectedScreen) return 0;
    return translations.filter((t) => {
      return (
        t.screen === selectedScreen &&
        t.es && // Tiene traducción en español
        t[selectedLanguage] // Tiene traducción en el idioma seleccionado
      );
    }).length;
  }, [selectedScreen, translations, selectedLanguage]);
  
  const missingOrMissingCount = totalKeys > 0 ? totalKeys - completeCount : 0;
  const coverage = totalKeys > 0 ? Math.round((completeCount / totalKeys) * 100) : 0;

  // Faltantes filtrados por pantalla (incluye missing y traducciones sin el idioma seleccionado)
  const filteredMissing = useMemo(() => {
    if (!selectedScreen) return [];
    
    const missingFromList = missing.filter((m) => m.screen === selectedScreen);
    
    // Agregar traducciones que no tienen traducción en el idioma seleccionado
    const translationsWithoutSelectedLang = translations
      .filter((t) => t.screen === selectedScreen && t.es && !t[selectedLanguage])
      .map((t) => ({
        id: t.id,
        screen: t.screen,
        key: t.key,
        baseValue: t.es || "",
      }));
    
    return [...missingFromList, ...translationsWithoutSelectedLang];
  }, [selectedScreen, missing, translations, selectedLanguage]);

  // Obtener el nombre del idioma seleccionado
  const selectedLanguageLabel = availableLanguages.find(lang => lang.code === selectedLanguage)?.label || selectedLanguage.toUpperCase();

  // Filtrar pantallas por búsqueda
  const filteredScreens = useMemo(() => {
    if (!screenSearch.trim()) return availableScreens;
    const searchLower = screenSearch.toLowerCase();
    return availableScreens.filter(screen => 
      screen.toLowerCase().includes(searchLower)
    );
  }, [availableScreens, screenSearch]);

  // Función para abrir modal de editar traducción (usando useCallback para estabilidad)
  const handleOpenEditModal = useCallback((record: TranslationRow) => {
    setIsEditMode(true);
    setCurrentTranslation(record);
    setTranslationModalVisible(true);
    translationForm.setFieldsValue({
      screen: record.screen,
      key: record.key,
      es: record.es || "",
      [selectedLanguage]: record[selectedLanguage] || "",
    });
  }, [selectedLanguage, translationForm]);

  // Función para eliminar traducción (usando useCallback para estabilidad)
  const handleDeleteTranslation = useCallback((record: TranslationRow) => {
    Modal.confirm({
      title: gestorTranslationPage?.delete_confirmation_title || "¿Estás seguro de eliminar esta traducción?",
      content: `${gestorTranslationPage?.delete_confirmation || "Se eliminará la traducción con key"} "${record.key}"`,
      okText: gestorTranslationPage?.delete_button || "Eliminar",
      okType: "danger",
      cancelText: gestorTranslationPage?.cancel_button || "Cancelar",
      onOk: async () => {
        try {
          // Llamar al API para eliminar la traducción
          const response = await deleteTranslation(record.screen, record.key);

          if (response?.responseStatus === "Success") {
            // Invalidar el caché después de eliminar
            invalidateTranslationsCache();
            
            // Recargar las traducciones desde el API para obtener los datos actualizados
            const updatedTranslations = await getScreenTranslations();
            if (updatedTranslations?.responseStatus === "Success" && updatedTranslations?.data) {
              const transformed = transformApiData(updatedTranslations, selectedLanguage);
              setTranslations(transformed.translations);
              setMissing(transformed.missing);
            }

            message.success(gestorTranslationPage?.translation_deleted || "Traducción eliminada correctamente");
          } else {
            message.error(response?.message || gestorTranslationPage?.error_delete_translation || "Error al eliminar la traducción");
          }
        } catch (error: any) {
          message.error(error?.response?.data?.message || gestorTranslationPage?.error_delete_translation || "Error al eliminar la traducción");
        }
      },
    });
  }, [gestorTranslationPage, selectedLanguage, transformApiData]);

  // Helper para badge de estado (memoizado)
  const getStatusBadge = useCallback((status: TranslationStatus) => {
    switch (status) {
      case "complete":
        return <StatusBadge $status="complete">{gestorTranslationPage?.status_complete || "Completa"}</StatusBadge>;
      case "missing":
        return <StatusBadge $status="missing">{gestorTranslationPage?.status_missing || "Faltante"}</StatusBadge>;
      default:
        return <StatusBadge $status="missing">{status}</StatusBadge>;
    }
  }, [gestorTranslationPage]);

  // Columnas para tabla de traducciones (debe estar antes del early return)
  const translationColumns: ColumnsType<TranslationRow> = useMemo(() => [
    {
      title: gestorTranslationPage?.translate_table?.[0]?.label || "Key",
      dataIndex: "key",
      key: "key",
      render: (text: string) => <KeyCell>{text}</KeyCell>,
    },
    {
      title: gestorTranslationPage?.translate_table?.[1]?.label || "Español",
      dataIndex: "es",
      key: "es",
      render: (text: string) => text || <EmptyText>—</EmptyText>,
    },
    {
      title: selectedLanguageLabel,
      dataIndex: selectedLanguage,
      key: selectedLanguage,
      render: (text: string) => {
        if (!text) {
          return <EmptyText>{gestorTranslationPage?.no_translate || "Sin traducción"}</EmptyText>;
        }
        return text;
      },
    },
    {
      title: gestorTranslationPage?.translate_table?.[3]?.label || "Estado",
      dataIndex: "status",
      key: "status",
      render: (_: TranslationStatus, record: TranslationRow) => {
        const hasTranslation = record.es && record[selectedLanguage];
        return getStatusBadge(hasTranslation ? "complete" : "missing");
      },
    },
    {
      title: "",
      key: "action",
      fixed: "right",
      width: 80,
      render: (_: unknown, record: TranslationRow) => {
        const items: MenuProps["items"] = [
          {
            key: "edit",
            label: gestorTranslationPage?.edit_translate || "Editar",
            icon: <EditOutlined />,
            onClick: () => handleOpenEditModal(record),
          },
          {
            key: "delete",
            label: gestorTranslationPage?.delete_button || "Eliminar",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleDeleteTranslation(record),
          },
        ];

        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ cursor: "pointer" }}
            />
          </Dropdown>
        );
      },
    },
  ], [selectedLanguage, selectedLanguageLabel, gestorTranslationPage, getStatusBadge, handleDeleteTranslation, handleOpenEditModal]);

  // Reglas de validación dinámicas para el campo screen
  const screenValidationRules = useMemo(() => [
    { 
      required: true, 
      message: isCreatingNewScreen 
        ? (gestorTranslationPage?.new_screen_required || "Por favor ingresa el nombre de la nueva pantalla")
        : (gestorTranslationPage?.select_option_required_screen || "Por favor selecciona una pantalla")
    }
  ], [isCreatingNewScreen, gestorTranslationPage]);

  // Early return para errores (después de todos los hooks)
  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setGestorTranslationPage(null);
      }}
    />;
  }

  // Función para agregar traducción
  const handleAddTranslation = async (values: { translation: string }) => {
    if (!currentMissing) return;

    try {
      // Construir el objeto traducciones
      const traducciones: { [key: string]: string } = {
        es: currentMissing.baseValue, // Español siempre presente
      };
      
      // Agregar el idioma seleccionado
      if (values.translation) {
        traducciones[selectedLanguage] = values.translation;
      }

      // Llamar al API para actualizar la traducción (usando PUT porque la pantalla y key ya existen)
      const response = await updateTranslation(
        currentMissing.screen,
        currentMissing.key,
        traducciones
      );

      if (response?.responseStatus === "Success") {
        // Invalidar el caché después de agregar
        invalidateTranslationsCache();
        
        // Recargar las traducciones desde el API para obtener el ID real
        const updatedTranslations = await getScreenTranslations();
        if (updatedTranslations?.responseStatus === "Success" && updatedTranslations?.data) {
          const transformed = transformApiData(updatedTranslations, selectedLanguage);
          setTranslations(transformed.translations);
          setMissing(transformed.missing);
        }

        // Cerrar modal y limpiar formulario
        setModalVisible(false);
        setCurrentMissing(null);
        form.resetFields();
        message.success(gestorTranslationPage?.translation_added || "Traducción agregada correctamente");
      } else {
        message.error(response?.message || gestorTranslationPage?.error_add_translation || "Error al agregar la traducción");
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || gestorTranslationPage?.error_add_translation || "Error al agregar la traducción");
    }
  };

  // Función para abrir modal de agregar traducción
  const handleOpenAddModal = (record: MissingRow) => {
    setCurrentMissing(record);
    setModalVisible(true);
    form.setFieldsValue({ translation: "" });
  };

  // Función para abrir modal de crear traducción
  const handleOpenCreateModal = () => {
    setIsEditMode(false);
    setCurrentTranslation(null);
    setIsCreatingNewScreen(false);
    setTranslationModalVisible(true);
    translationForm.setFieldsValue({ screen: selectedScreen });
  };


  // Función para guardar traducción (crear o editar)
  const handleSaveTranslation = async (values: {
    screen: string;
    key: string;
    es: string;
    [key: string]: any;
  }) => {
    const selectedLangValue = values[selectedLanguage] || "";
    
    // Si es una nueva pantalla que no existe en la lista, agregarla
    if (values.screen && !availableScreens.includes(values.screen)) {
      setAvailableScreens((prev) => [...prev, values.screen]);
      setSelectedScreen(values.screen);
    }
    
    if (isEditMode && currentTranslation) {
      // Modo edición - llamar al API
      try {
        // Construir el objeto traducciones con todos los idiomas del formulario
        const traducciones: { [key: string]: string } = {};
        
        // Agregar español (siempre presente)
        if (values.es) {
          traducciones.es = values.es;
        }
        
        // Agregar el idioma seleccionado
        if (selectedLangValue) {
          traducciones[selectedLanguage] = selectedLangValue;
        }
        
        // Agregar cualquier otro idioma que pueda estar en el formulario
        Object.keys(values).forEach((key) => {
          if (key !== 'screen' && key !== 'key' && key !== 'es' && values[key] && typeof values[key] === 'string') {
            traducciones[key] = values[key];
          }
        });

        // Llamar al API para actualizar la traducción
        const response = await updateTranslation(
          currentTranslation.screen,
          currentTranslation.key,
          traducciones
        );

        if (response?.responseStatus === "Success") {
          // Invalidar el caché después de actualizar
          invalidateTranslationsCache();
          
          // Recargar las traducciones desde el API para obtener los datos actualizados
          const updatedTranslations = await getScreenTranslations();
          if (updatedTranslations?.responseStatus === "Success" && updatedTranslations?.data) {
            const transformed = transformApiData(updatedTranslations, selectedLanguage);
            setTranslations(transformed.translations);
            setMissing(transformed.missing);
          }

          message.success(gestorTranslationPage?.translation_updated || "Traducción actualizada correctamente");
          setTranslationModalVisible(false);
          setCurrentTranslation(null);
          setIsEditMode(false);
          setIsCreatingNewScreen(false);
          translationForm.resetFields();
        } else {
          message.error(response?.message || gestorTranslationPage?.error_update_translation || "Error al actualizar la traducción");
        }
      } catch (error: any) {
        message.error(error?.response?.data?.message || gestorTranslationPage?.error_update_translation || "Error al actualizar la traducción");
      }
    } else {
      // Modo creación - llamar al API
      try {
        // Construir el objeto traducciones con todos los idiomas del formulario
        const traducciones: { [key: string]: string } = {};
        
        // Agregar español (siempre presente)
        if (values.es) {
          traducciones.es = values.es;
        }
        
        // Agregar el idioma seleccionado
        if (selectedLangValue) {
          traducciones[selectedLanguage] = selectedLangValue;
        }
        
        // Agregar cualquier otro idioma que pueda estar en el formulario
        Object.keys(values).forEach((key) => {
          if (key !== 'screen' && key !== 'key' && key !== 'es' && values[key] && typeof values[key] === 'string') {
            traducciones[key] = values[key];
          }
        });

        // Llamar al API para crear la traducción
        const response = await createTranslation({
          key: values.key,
          screen: values.screen,
          traducciones: traducciones,
        });

        if (response?.responseStatus === "Success") {
          // Invalidar el caché después de crear
          invalidateTranslationsCache();
          
          // Recargar las traducciones desde el API para obtener el ID real
          const updatedTranslations = await getScreenTranslations();
          if (updatedTranslations?.responseStatus === "Success" && updatedTranslations?.data) {
            const transformed = transformApiData(updatedTranslations, selectedLanguage);
            setTranslations(transformed.translations);
            setMissing(transformed.missing);
          }

          message.success(gestorTranslationPage?.translation_created || "Traducción creada correctamente");
          setTranslationModalVisible(false);
          setCurrentTranslation(null);
          setIsEditMode(false);
          setIsCreatingNewScreen(false);
          translationForm.resetFields();
        } else {
          message.error(response?.message || gestorTranslationPage?.error_create_translation || "Error al crear la traducción");
        }
      } catch (error: any) {
        message.error(error?.response?.data?.message || gestorTranslationPage?.error_create_translation || "Error al crear la traducción");
      }
    }
  };

  // Función para agregar nuevo idioma
  const handleAddLanguage = (values: { code: string; label: string }) => {
    const code = values.code.toLowerCase();
    if (availableLanguages.find(lang => lang.code === code)) {
      message.error(gestorTranslationPage?.language_already_exists || "Este idioma ya existe");
      return;
    }
    
    setAvailableLanguages((prev) => [...prev, { code, label: values.label }]);
    setAddLanguageModalVisible(false);
    addLanguageForm.resetFields();
    message.success(gestorTranslationPage?.language_added_success || "Idioma agregado correctamente");
  };

  // Función para cerrar modal
  const handleCloseTranslationModal = () => {
    setTranslationModalVisible(false);
    setCurrentTranslation(null);
    setIsEditMode(false);
    setIsCreatingNewScreen(false);
    translationForm.resetFields();
  };


  // Columnas para tabla de faltantes
  const missingColumns: ColumnsType<MissingRow> = [
    {
      title: gestorTranslationPage?.pending_translate_table?.[0]?.label || "Pantalla",
      dataIndex: "screen",
      key: "screen",
    },
    {
      title: gestorTranslationPage?.pending_translate_table?.[1]?.label || "Key",
      dataIndex: "key",
      key: "key",
      render: (text: string) => <KeyCell>{text}</KeyCell>,
    },
    {
      title: gestorTranslationPage?.pending_translate_table?.[2]?.label || "Valor base (ES)",
      dataIndex: "baseValue",
      key: "baseValue",
    },
    {
      title: "",
      key: "action",
      render: (_: unknown, record: MissingRow) => (
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenAddModal(record)}
        >
          {gestorTranslationPage?.Add_button || "Agregar traducción"}
        </Button>
      ),
    },
  ];

  return (
    <TranslationContainer>
      <SidebarStyled>
        {isLoadingContent || isLoadingTranslations ? (
          <>
            <div>
              <Skeleton.Input active size="large" style={{ width: "100%", marginBottom: 8 }} />
              <Skeleton.Input active size="default" style={{ width: "80%" }} />
            </div>

            <SidebarSection>
              <Skeleton.Input active size="default" style={{ width: "60%", marginBottom: 8 }} />
              <Skeleton.Input active size="large" style={{ width: "100%" }} />
            </SidebarSection>

            <SidebarSection style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
              <Skeleton.Input active size="default" style={{ width: "50%", marginBottom: 8 }} />
              <Skeleton.Input active size="large" style={{ width: "100%", marginBottom: 8 }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Skeleton.Button active size="default" style={{ width: "100%" }} />
                <Skeleton.Button active size="default" style={{ width: "100%" }} />
                <Skeleton.Button active size="default" style={{ width: "100%" }} />
              </div>
            </SidebarSection>
          </>
        ) : (
          <>
            <div>
              <SidebarTitle>{gestorTranslationPage?.sb_title || "Gestor de Traducciones"}</SidebarTitle>
              <SidebarSubtitle>{gestorTranslationPage?.sb_description || "Gestiona tus traducciones"}</SidebarSubtitle>
            </div>

            <SidebarSection>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <SidebarLabel>{gestorTranslationPage?.sb_language || "Idioma actual"}</SidebarLabel>
                {/* <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setAddLanguageModalVisible(true)}
                  style={{ padding: 0, height: "auto" }}
                >
                </Button> */}
              </div>
              <Select
                value={selectedLanguage}
                onChange={(value) => setSelectedLanguage(value)}
                style={{ width: "100%" }}
                options={availableLanguages.map(lang => ({
                  label: lang.label,
                  value: lang.code,
                }))}
              />
            </SidebarSection>

            <SidebarSection style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
              <SidebarLabel>{gestorTranslationPage?.sb_screen || "PANTALLAS"}</SidebarLabel>
              <Input
                placeholder={gestorTranslationPage?.search_spaceholder || "Buscar pantalla..."}
                prefix={<SearchOutlined />}
                value={screenSearch}
                onChange={(e) => setScreenSearch(e.target.value)}
                style={{ marginBottom: "8px", borderRadius: "8px" }}
                allowClear
              />
              <SidebarSectionScrollable>
                {filteredScreens.map((screen) => (
                  <ScreenButton
                    key={screen}
                    $active={selectedScreen === screen}
                    onClick={() => setSelectedScreen(screen)}
                  >
                    {screen}
                  </ScreenButton>
                ))}
              </SidebarSectionScrollable>
            </SidebarSection>
          </>
        )}
      </SidebarStyled>

      <MainContent>
        {isLoadingContent || isLoadingTranslations ? (
          <>
            <header>
              <Skeleton.Input active size="large" style={{ width: 300, marginBottom: 8 }} />
              <Skeleton.Input active size="default" style={{ width: "80%" }} />
            </header>

            <MetricsContainer>
              <MetricCard>
                <Skeleton.Input active size="default" style={{ width: 150, marginBottom: 8 }} />
                <Skeleton.Input active size="large" style={{ width: 80 }} />
              </MetricCard>
              <MetricCard>
                <Skeleton.Input active size="default" style={{ width: 150, marginBottom: 8 }} />
                <Skeleton.Input active size="large" style={{ width: 80 }} />
              </MetricCard>
              <MetricCard>
                <Skeleton.Input active size="default" style={{ width: 200, marginBottom: 8 }} />
                <Skeleton.Input active size="large" style={{ width: 80 }} />
              </MetricCard>
            </MetricsContainer>

            <SearchContainer>
              <Skeleton.Input active size="large" style={{ flex: 1, height: 40 }} />
              <Skeleton.Button active size="large" style={{ width: 180 }} />
            </SearchContainer>

            <TabsContainer>
              <Skeleton.Button active size="large" style={{ width: 150 }} />
              <Skeleton.Button active size="large" style={{ width: 200 }} />
            </TabsContainer>

            <TableCard>
              <Skeleton active paragraph={{ rows: 5 }} />
            </TableCard>
          </>
        ) : (
          <>
            <header>
              <LargeHeadingStyled>{gestorTranslationPage?.title || "Gestor de Traducciones"} {selectedScreen}</LargeHeadingStyled>
              <p style={{ fontSize: "16px", color: "#8c8c8c", marginTop: "4px" }}>
                {gestorTranslationPage?.description || "Revisa las traducciones, filtra por texto y controla qué frases todavía faltan por traducir."}
              </p>
            </header>

            <SearchContainer>
              <Input
                size="large"
                placeholder={gestorTranslationPage?.search_input || "Buscar por key o texto (Ej: login o BTN_SAVE, etc.)"}
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ borderRadius: "12px", width: "400px" }}
              />
              <Button
                type="primary"
                size="large"
                icon={<FileAddOutlined />}
                onClick={handleOpenCreateModal}
                style={{ borderRadius: "12px" }}
              >
                {gestorTranslationPage?.new_translate || "Crear traducción"}
              </Button>
            </SearchContainer>

        <MetricsContainer>
          <MetricCard>
            <MetricLabel>{gestorTranslationPage?.key_dashboard || "Keys en pantalla"}</MetricLabel>
            <MetricValue>{totalKeys}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>{gestorTranslationPage?.translate_dashboard || "Traducciones completas"}</MetricLabel>
            <MetricValue>{completeCount}</MetricValue>
          </MetricCard>
          <MetricCard $warning={coverage !== 100 && coverage < 100} $success={coverage === 100}>
            <MetricLabel>
              {gestorTranslationPage?.cover_language_dashboard || "Cobertura en "} {selectedLanguageLabel.toLowerCase()}
              <span style={{ fontSize: "14px", fontWeight: "normal", marginLeft: "8px" }}>
                ({missingOrMissingCount} {gestorTranslationPage?.pending_dashboard || "pendientes por traducir"})
              </span>
            </MetricLabel>
            <MetricValue>{coverage}%</MetricValue>
          </MetricCard>
        </MetricsContainer>

        <TabsContainer>
          <TabButton $active={activeTab === "translations"} onClick={() => setActiveTab("translations")}>
            {gestorTranslationPage?.translate_button || "Traducciones"}
          </TabButton>
          <TabButton $active={activeTab === "missing"} onClick={() => setActiveTab("missing")}>
            {gestorTranslationPage?.pending_translate_button || "Pendientes por traducir"}
          </TabButton>
        </TabsContainer>

        <TableCard>
          {activeTab === "translations" && (
            <Table<TranslationRow>
              columns={translationColumns}
              dataSource={filteredTranslations}
              rowKey="id"
              pagination={{
                defaultPageSize: 5,
                showSizeChanger: true,
                style: { marginRight: "30px" },
              }}
              locale={{
                emptyText: gestorTranslationPage?.empty_text_translations || "No hay traducciones para esta pantalla o criterio de búsqueda.",
              }}
            />
          )}

          {activeTab === "missing" && (
            <Table<MissingRow>
              columns={missingColumns}
              dataSource={filteredMissing}
              rowKey="id"
              pagination={{
                defaultPageSize: 5,
                showSizeChanger: true,
                style: { marginRight: "30px" },
              }}
              locale={{
                emptyText: gestorTranslationPage?.empty_text_missing || "No hay frases pendientes para esta pantalla 🎉",
              }}
            />
          )}
        </TableCard>
          </>
        )}
      </MainContent>

      <Modal
        title={gestorTranslationPage?.Add_button || "Agregar traducción"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setCurrentMissing(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} onFinish={handleAddTranslation} layout="vertical">
          <Form.Item label={gestorTranslationPage?.key_field_label || "Key"}>
            <Input value={currentMissing?.key} disabled />
          </Form.Item>
          <Form.Item label={gestorTranslationPage?.translate_table?.[1]?.label || "Español (ES)"}>
            <Input value={currentMissing?.baseValue} disabled />
          </Form.Item>
          <Form.Item
            label={selectedLanguageLabel}
            name="translation"
            rules={[{ required: true, message: `${gestorTranslationPage?.translation_required_message_prefix || "Por favor ingresa la traducción en"} ${selectedLanguageLabel}` }]}
          >
            <Input placeholder={gestorTranslationPage?.text_input || "Ingresa el texto"} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              onClick={() => {
                setModalVisible(false);
                setCurrentMissing(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              {gestorTranslationPage?.cancel_button || "Cancelar"}
            </Button>
            <Button type="primary" htmlType="submit">
              {gestorTranslationPage?.Add_button || "Agregar"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={isEditMode ? gestorTranslationPage?.edit_translate || "Editar traducción" : gestorTranslationPage?.new_translate || "Crear traducción"}
        open={translationModalVisible}
        onCancel={handleCloseTranslationModal}
        footer={null}
        width={600}
      >
        <Form form={translationForm} onFinish={handleSaveTranslation} layout="vertical">
          <Form.Item
            label={gestorTranslationPage?.title|| "Pantalla"}
            name="screen"
            rules={screenValidationRules}
            validateTrigger={['onChange', 'onBlur']}
          >
            {isCreatingNewScreen ? (
              <Input.Group compact style={{ display: "flex" }}>
                <Input
                  placeholder={gestorTranslationPage?.new_screen_placeholder || "Ingresa el nombre de la nueva pantalla"}
                  style={{ flex: 1 }}
                  disabled={isEditMode}
                  onChange={(e) => {
                    const value = e.target.value;
                    translationForm.setFieldValue("screen", value);
                    if (value && value.trim()) {
                      translationForm.setFields([{ name: 'screen', errors: [] }]);
                    }
                  }}
                />
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setIsCreatingNewScreen(false);
                    translationForm.setFieldValue("screen", undefined);
                    translationForm.setFields([{ name: 'screen', errors: [] }]);
                  }}
                  disabled={isEditMode}
                />
              </Input.Group>
            ) : (
              <Input.Group compact style={{ display: "flex" }}>
                <Select
                  placeholder={gestorTranslationPage?.select_option || "Selecciona una pantalla"}
                  options={availableScreens.map((screen) => ({ label: screen, value: screen }))}
                  disabled={isEditMode}
                  style={{ flex: 1 }}
                  onChange={(value) => {
                    translationForm.setFieldValue("screen", value);
                    if (value) {
                      translationForm.setFields([{ name: 'screen', errors: [] }]);
                    }
                  }}
                />
                {!isEditMode && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setIsCreatingNewScreen(true);
                      translationForm.setFieldValue("screen", undefined);
                      translationForm.setFields([{ name: 'screen', errors: [] }]);
                    }}
                  />
                )}
              </Input.Group>
            )}
          </Form.Item>
          <Form.Item
            label={gestorTranslationPage?.key_field_label || "Key"}
            name="key"
            rules={[{ required: true, message: gestorTranslationPage?.text_input_required_key || "Por favor ingresa la key" }]}
          >
            <Input placeholder={gestorTranslationPage?.key_field_placeholder || "Ej: BTN_SAVE, LBL_NAME, etc."} disabled={isEditMode} />
          </Form.Item>
          {!isEditMode && (
            <>
              <Form.Item
                label={gestorTranslationPage?.translate_table?.[1]?.label || "Español (ES)"}
                name="es"
                rules={[{ required: true, message: gestorTranslationPage?.text_input_required_es || "Por favor ingresa la traducción en español" }]}
              >
                <Input placeholder={gestorTranslationPage?.text_input || "Ingresa el texto"} />
              </Form.Item>
              <Form.Item
                label={selectedLanguageLabel}
                name={selectedLanguage}
                rules={[{ required: true, message: `${gestorTranslationPage?.translation_required_message_prefix || "Por favor ingresa la traducción en"} ${selectedLanguageLabel}` }]}
              >
                <Input placeholder={gestorTranslationPage?.text_input || "Ingresa el texto"} />
              </Form.Item>
            </>
          )}
          {isEditMode && (
            <>
              <Form.Item
                label={gestorTranslationPage?.translate_table?.[1]?.label || "Español (ES)"}
                name="es"
                rules={[{ required: true, message: gestorTranslationPage?.text_input_required_es || "Por favor ingresa la traducción en español" }]}
              >
                <Input placeholder={gestorTranslationPage?.text_input || "Ingresa el texto"} />
              </Form.Item>
              <Form.Item
                label={selectedLanguageLabel}
                name={selectedLanguage}
                rules={[{ required: true, message: `${gestorTranslationPage?.translation_required_message_prefix || "Por favor ingresa la traducción en"} ${selectedLanguageLabel}` }]}
              >
                <Input placeholder={gestorTranslationPage?.text_input || "Ingresa el texto"} />
              </Form.Item>
            </>
          )}
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button onClick={handleCloseTranslationModal} style={{ marginRight: 8 }}>
              {gestorTranslationPage?.cancel_button || "Cancelar"}
            </Button>
            <Button type="primary" htmlType="submit">
              {isEditMode ? gestorTranslationPage?.save_button || "Guardar" : gestorTranslationPage?.create_button || "Crear"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={gestorTranslationPage?.language_add_title || "Agregar nuevo idioma"}
        open={addLanguageModalVisible}
        onCancel={() => {
          setAddLanguageModalVisible(false);
          addLanguageForm.resetFields();
        }}
        footer={null}
      >
        <Form form={addLanguageForm} onFinish={handleAddLanguage} layout="vertical">
          <Form.Item
            label={gestorTranslationPage?.code_language || "Código del idioma (ej: de, fr, it)"}
            name="code"
            rules={[
              { required: true, message: gestorTranslationPage?.enter_message_language || "Por favor ingresa el código del idioma" },
              { pattern: /^[a-z]{2}$/, message: gestorTranslationPage?.required_language_message || "El código debe tener 2 letras minúsculas (ej: de, fr, it)" }
            ]}
          >
            <Input placeholder={gestorTranslationPage?.example_message || "Ej: de, fr, it"} maxLength={2} />
          </Form.Item>
          <Form.Item
            label={gestorTranslationPage?.language_name || "Nombre del idioma"}
            name="label"
            rules={[{ required: true, message: gestorTranslationPage?.enter_language_name || "Por favor ingresa el nombre del idioma" }]}
          >
            <Input placeholder={gestorTranslationPage?.example_message_name || "Ej: Alemán (DE), Francés (FR), Italiano (IT)"} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              onClick={() => {
                setAddLanguageModalVisible(false);
                addLanguageForm.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              {gestorTranslationPage?.cancel_button || "Cancelar"}
            </Button>
            <Button type="primary" htmlType="submit">
              {gestorTranslationPage?.add_language_button || "Agregar"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </TranslationContainer>
  );
};

export default TranslationGestor;